import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { GraduationCap, Globe, MapPin, ArrowRight } from 'lucide-react';

const GRADUATION_IMAGE = 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/6nufkuof_iStock-180250187.jpg';

export default function OutcomesSection() {
  const { t, language } = useLanguage();

  const destinations = language === 'fr' ? [
    { icon: MapPin, label: 'France' },
    { icon: MapPin, label: 'Europe' },
    { icon: MapPin, label: 'Royaume-Uni' },
    { icon: MapPin, label: 'Amérique du Nord' },
  ] : [
    { icon: MapPin, label: 'France' },
    { icon: MapPin, label: 'Europe' },
    { icon: MapPin, label: 'United Kingdom' },
    { icon: MapPin, label: 'North America' },
  ];

  return (
    <section
      data-testid="outcomes-section"
      className="relative py-24 md:py-32 overflow-hidden bg-[#14274B]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-[#66CA9D] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.outcomes.overline}
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-light text-white leading-tight tracking-tight mb-6"
              style={{ fontFamily: 'Gabarito, sans-serif' }}
            >
              {t.outcomes.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-white/70 leading-relaxed mb-8"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.outcomes.subtitle}
            </motion.p>

            {/* Destinations */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-4"
            >
              {destinations.map((dest, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 bg-white/10 px-4 py-2"
                >
                  <dest.icon className="w-4 h-4 text-[#66CA9D]" />
                  <span className="text-white text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {dest.label}
                  </span>
                </div>
              ))}
            </motion.div>

            {/* Internal Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link to="/diploma" className="flex items-center gap-2 text-[#66CA9D] hover:text-white transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Découvrir le diplôme' : 'Discover the diploma'} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/admissions" className="flex items-center gap-2 text-[#2D78F6] hover:text-white transition-colors text-sm font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Processus d\'admission' : 'Admission process'} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={GRADUATION_IMAGE}
                alt="Campus universitaire américain — U.S. High School Diploma OFA France"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-[#66CA9D] hidden lg:block" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
